import React from "react";
import {
  Typography,
  Box,
  Accordion,
  AccordionGroup,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Button,
  Link,
} from "@mui/joy";
import { accordionDetailsClasses, accordionSummaryClasses } from "@mui/joy";

const actions = [
  {
    title: "Find & Contact Your Representative",
    content: (
      <Typography component="div" sx={{ fontSize: "15px", color: "#535862", lineHeight: 1.7 }}>
        You have several ways to reach your elected officials:
        <Box component="ul" sx={{ mt: "10px", mb: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "8px" }}>
          <li>
            Use the <Link href="/legislators" sx={{ color: "#0075b2", fontWeight: 600 }}>Legislators</Link> page
            to search by state for your senators and house representatives.
          </li>
          <li>
            Find a bill in <Link href="/bills" sx={{ color: "#0075b2", fontWeight: 600 }}>Bills &amp; Legislation</Link>,
            open its detail page, and use "Contact your Representative" to send a personalized email with our template.
          </li>
          <li>
            Go directly to <Link href="https://www.house.gov/representatives/find-your-representative" target="_blank" rel="noopener" sx={{ color: "#0075b2", fontWeight: 600 }}>House.gov</Link> and
            enter your zip code to find your district representative.
          </li>
        </Box>
      </Typography>
    ),
  },
  {
    title: "Petitions",
    content: (
      <Typography component="div" sx={{ fontSize: "15px", color: "#535862", lineHeight: 1.7 }}>
        Petitions show lawmakers that an issue has public support. While the White House petition
        site was dissolved in 2021, impactful petitions still happen elsewhere:
        <Box component="ul" sx={{ mt: "10px", mb: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "8px" }}>
          <li>
            <strong>Start one:</strong> Use <Link href="https://www.change.org/" target="_blank" rel="noopener" sx={{ color: "#0075b2", fontWeight: 600 }}>Change.org</Link> to
            launch your own campaign around a specific bill or policy.
          </li>
          <li>
            <strong>Sign and share:</strong> Adding your name and spreading the word amplifies reach
            and signals to officials that constituents care.
          </li>
        </Box>
      </Typography>
    ),
  },
  {
    title: "Public Comments",
    content: (
      <Typography component="div" sx={{ fontSize: "15px", color: "#535862", lineHeight: 1.7 }}>
        Before finalizing regulations, federal agencies are required to accept public feedback.
        Your comment is part of the official record:
        <Box component="ul" sx={{ mt: "10px", mb: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "8px" }}>
          <li>
            Find open comment periods at <Link href="https://www.regulations.gov/" target="_blank" rel="noopener" sx={{ color: "#0075b2", fontWeight: 600 }}>Regulations.gov</Link>.
          </li>
          <li>
            Be specific: explain how the proposed rule affects you or your community with concrete detail.
          </li>
          <li>
            Submit before the deadline — agencies must review all timely comments before issuing a final rule.
          </li>
        </Box>
      </Typography>
    ),
  },
  {
    title: "Support Organizations Making a Difference",
    content: (
      <Typography component="div" sx={{ fontSize: "15px", color: "#535862", lineHeight: 1.7 }}>
        Civic organizations multiply individual voices. Donating, volunteering, or even sharing
        their work on social media extends their reach:
        <Box component="ul" sx={{ mt: "10px", mb: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "8px" }}>
          <li>
            <Link href="https://www.aclu.org/" target="_blank" rel="noopener" sx={{ color: "#0075b2", fontWeight: 600 }}>ACLU</Link> — Defends civil liberties and constitutional rights.
          </li>
          <li>
            <Link href="https://www.commoncause.org/" target="_blank" rel="noopener" sx={{ color: "#0075b2", fontWeight: 600 }}>Common Cause</Link> — Advocates for fair elections and government accountability.
          </li>
          <li>
            <Link href="https://www.lwv.org/" target="_blank" rel="noopener" sx={{ color: "#0075b2", fontWeight: 600 }}>League of Women Voters</Link> — Works to empower voters and defend democracy.
          </li>
          <li>
            <Link href="https://www.splcenter.org/" target="_blank" rel="noopener" sx={{ color: "#0075b2", fontWeight: 600 }}>Southern Poverty Law Center</Link> — Fights against hate and extremism.
          </li>
        </Box>
      </Typography>
    ),
  },
];

const TakeActions = () => {
  const goToLegislators = (e) => {
    e.preventDefault();
    window.history.pushState({}, "", "/legislators");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return (
    <Box
      sx={{
        borderTop: "1px solid rgba(0,0,0,0.07)",
        backgroundColor: "#f8fafc",
      }}
    >
      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          px: { xs: "1.25rem", md: "2rem" },
          py: { xs: "3.5rem", md: "5rem" },
        }}
      >
        {/* Section header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "flex-end" }}
          justifyContent="space-between"
          spacing={3}
          sx={{ mb: { xs: "2.5rem", md: "3.5rem" } }}
        >
          <Box>
            <Typography
              component="p"
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#0075b2",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                mb: "10px",
              }}
            >
              Get involved
            </Typography>
            <Typography
              level="h2"
              sx={{
                fontSize: { xs: "28px", md: "36px" },
                fontWeight: 700,
                color: "#171a1c",
                lineHeight: 1.2,
              }}
            >
              Take Action
            </Typography>
          </Box>
          <Button
            onClick={goToLegislators}
            sx={{
              backgroundColor: "#003049",
              color: "white",
              fontWeight: 600,
              fontSize: "15px",
              borderRadius: "6px",
              minHeight: "44px",
              px: "24px",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#00243a" },
            }}
          >
            Find your representative
          </Button>
        </Stack>

        {/* Accordion */}
        <AccordionGroup
          variant="plain"
          transition="0.18s ease"
          sx={{
            backgroundColor: "white",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: "4px",
            overflow: "hidden",
            [`& .${accordionSummaryClasses.button}`]: {
              px: "28px",
              py: "20px",
              "&:hover": { backgroundColor: "rgba(0,117,178,0.04)" },
            },
            [`& .${accordionDetailsClasses.content}.${accordionDetailsClasses.expanded}`]: {
              px: "28px",
              pb: "24px",
              pt: 0,
            },
          }}
        >
          {actions.map((action, i) => (
            <Accordion key={i} color="neutral">
              <AccordionSummary
                indicator={null}
                sx={{
                  borderTop: i === 0 ? "none" : "1px solid rgba(0,0,0,0.07)",
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                  <Typography sx={{ fontSize: "17px", fontWeight: 600, color: "#171a1c", textAlign: "left" }}>
                    {action.title}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#0075b2", fontWeight: 600, ml: "16px", flexShrink: 0 }}>
                    +
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {action.content}
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionGroup>
      </Box>
    </Box>
  );
};

export default TakeActions;
